/**
 * Concluir e cobrar em tela cheia (docs/design, "Celular: concluir e cobrar"): tocar numa forma
 * conclui (2º toque a partir da Agenda). Pacote: um botão só, desconta a sessão.
 * Com estoque (docs/design/pranchas/12-estoque.png): a lista do que ela vai pagar, o serviço e os
 * produtos vendidos junto; a pergunta soma tudo e as formas continuam no mesmo lugar.
 */
import {
  type SaleItemInput,
  type StockLevel,
  activePackageFor,
  formatHourOf,
  formatMoneyShort,
  formatStockQty,
  parseMoney,
  relativeDayLabel,
  todayIn,
  utcToZoned,
} from '@lindeza/core';
import { useLiveQuery, useProfessional, useSellable, useServiceSupplies } from '@lindeza/core/hooks';
import { useEffect, useRef, useState } from 'react';
import {
  IconArrowLeft,
  IconBanknote,
  IconCalendarClock,
  IconChevronUp,
  IconCreditCard,
  IconPackage,
  IconPercent,
  IconPlus,
  IconQrCode,
} from '../../components/Icons';
import { Stepper } from '../../components/Stepper';
import { Avatar, Field } from '../../components/ui';
import { useComplete } from './useComplete';

export function ChargeScreen({ id, onClose }: { id: string; onClose: () => void }) {
  const prof = useProfessional();
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  const complete = useComplete();
  const [busy, setBusy] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discount, setDiscount] = useState('');
  /** embalagens de cada produto vendido junto */
  const [cart, setCart] = useState<Record<string, number>>({});
  const [picking, setPicking] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const data = useLiveQuery(
    async (r) => {
      const a = await r.db.get('appointment', id);
      if (!a) return null;
      const [client, service, packages, appointments] = await Promise.all([
        r.db.get('client', a.client_id),
        r.db.get('service', a.service_id),
        r.db.all('package'),
        r.db.all('appointment'),
      ]);
      return { a, client, service, pkg: activePackageFor(packages, appointments, a.client_id, a.service_id, todayIn(tz)) };
    },
    [id, tz],
    ['appointment', 'client', 'service', 'package'],
  );
  const sellable = useSellable();
  const supplies = useServiceSupplies(data?.a.service_id ?? null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!data) return null;
  const { a, client, service, pkg } = data;
  const discountCents = parseMoney(discount) ?? 0;
  const due = Math.max(0, a.price_cents - discountCents);
  const local = utcToZoned(a.start_utc, tz);
  const her = client?.name.split(' ')[0] ?? 'ela';

  // Produtos só entram quando ela paga o atendimento (no pacote não há forma de pagamento).
  const levels = new Map((sellable ?? []).map((l) => [l.product.id, l]));
  const withProducts = !pkg && levels.size > 0;
  const items = Object.entries(cart)
    .map(([productId, packs]) => ({ level: levels.get(productId), packs }))
    .filter((i): i is { level: StockLevel; packs: number } => Boolean(i.level) && i.packs > 0);
  const productsCents = items.reduce((s, i) => s + i.packs * (i.level.product.price_cents ?? 0), 0);
  const total = due + productsCents;
  const toPick = (sellable ?? []).filter((l) => !cart[l.product.id]);
  const used = usedText(supplies?.items ?? []);

  const setPacks = (productId: string, packs: number) => setCart((c) => ({ ...c, [productId]: packs }));

  async function pay(method: 'pix' | 'cash' | 'card' | null) {
    setBusy(true);
    const products: SaleItemInput[] = withProducts ? items.map((i) => ({ productId: i.level.product.id, packs: i.packs })) : [];
    const ok = await complete(a.id, method, discountCents, products);
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <div
      className={`pay${withProducts ? ' pay--cart' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Concluir e cobrar ${client?.name ?? ''}`}
      tabIndex={-1}
      ref={ref}
    >
      <header className="pay-top">
        <Avatar name={client?.name ?? '?'} size="lg" />
        <b className="pay-name">{client?.name ?? 'Cliente'}</b>
        <span className="pay-svc">{service?.name ?? 'Serviço'}</span>
        <span className="pay-time">
          {relativeDayLabel(local.date, todayIn(tz))}, das {formatHourOf(a.start_utc, tz)} às {formatHourOf(a.end_utc, tz)}
        </span>
      </header>

      <div className="pay-body">
        {pkg ? (
          <>
            <h2 className="pay-q">
              Pacote de {her}: <b>{pkg.balance.remaining}</b> de {pkg.balance.total} sessões no saldo.
            </h2>
            <div className="pay-grid">
              <button className="pay-opt pay-opt--wide" disabled={busy} onClick={() => void pay(null)}>
                <IconPackage /> Concluir · desconta 1 sessão
              </button>
            </div>
          </>
        ) : (
          <>
            {withProducts && (
              <section className="pay-cart" aria-label="O que ela vai pagar">
                <div className="pay-cart-row pay-cart-svc">
                  <span className="pay-cart-t">
                    <span className="ellipsis">{service?.name ?? 'Serviço'}</span>
                  </span>
                  <b className="pay-cart-amt">{formatMoneyShort(due)}</b>
                </div>
                {items.map(({ level, packs }) => {
                  const max = Math.floor(level.qty / level.product.pack_size);
                  return (
                    <div key={level.product.id} className="pay-cart-row">
                      <span className="pay-cart-t">
                        <b className="ellipsis">{level.product.name}</b>
                        <span>
                          {formatMoneyShort(level.product.price_cents ?? 0)} cada · tem {max}
                        </span>
                      </span>
                      <Stepper label={level.product.name} value={packs} max={max} onChange={(v) => setPacks(level.product.id, v)} />
                    </div>
                  );
                })}
                {picking && toPick.length > 0 && (
                  <ul className="pay-pick" aria-label="Produtos à venda">
                    {toPick.map((l) => (
                      <li key={l.product.id}>
                        <button
                          type="button"
                          className="pay-pick-it"
                          onClick={() => {
                            setPacks(l.product.id, 1);
                            setPicking(false);
                          }}
                          aria-label={`Adicionar ${l.product.name}`}
                        >
                          <span className="pay-cart-t">
                            <b className="ellipsis">{l.product.name}</b>
                            <span>
                              {formatMoneyShort(l.product.price_cents ?? 0)} · tem {Math.floor(l.qty / l.product.pack_size)}
                            </span>
                          </span>
                          <span className="pay-pick-plus" aria-hidden="true">
                            <IconPlus />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {toPick.length > 0 && (
                  <button type="button" className="pay-add" aria-expanded={picking} onClick={() => setPicking((v) => !v)}>
                    {picking ? <IconChevronUp /> : <IconPlus />}
                    {picking ? 'Fechar a lista' : items.length > 0 ? 'Vendeu outro produto?' : 'Vendeu algum produto?'}
                  </button>
                )}
              </section>
            )}
            <h2 className="pay-q">
              Como {her} pagou os <b data-testid="due">{formatMoneyShort(total).replace(' ', ' ')}</b>?
            </h2>
            <div className="pay-grid">
              <button className="pay-opt" disabled={busy} onClick={() => void pay('pix')}>
                <IconQrCode />
                Pix
              </button>
              <button className="pay-opt" disabled={busy} onClick={() => void pay('cash')}>
                <IconBanknote />
                Dinheiro
              </button>
              <button className="pay-opt" disabled={busy} onClick={() => void pay('card')}>
                <IconCreditCard />
                Cartão
              </button>
              <button className="pay-opt" disabled={busy} onClick={() => void pay(null)}>
                <IconCalendarClock />
                Receber depois
              </button>
            </div>
            {showDiscount && (
              <div className="pay-discount">
                <Field label="Desconto" htmlFor="pay-discount" hint={`O serviço fica ${formatMoneyShort(due)}`}>
                  <input id="pay-discount" className="input" inputMode="decimal" autoFocus placeholder="0,00" value={discount} onChange={(e) => setDiscount(e.target.value)} />
                </Field>
              </div>
            )}
          </>
        )}
        <div className="pay-sec">
          <button className="btn btn--ghost" onClick={onClose}>
            <IconArrowLeft /> Voltar
          </button>
          {!pkg && !showDiscount && (
            <button className="btn btn--ghost pay-disc" onClick={() => setShowDiscount(true)}>
              <IconPercent /> Dar desconto
            </button>
          )}
        </div>
        <p className="pay-note">{used ? `Ao concluir, sai do estoque: ${used}.` : 'Toque uma vez para concluir. Dá para desfazer.'}</p>
      </div>
    </div>
  );
}

/** "4 g de gel construtor e 1 lixa 100/180": o que o serviço gasta, para o rodapé. */
function usedText(items: Array<{ supply: { qty: number }; product: { name: string; unit: 'un' | 'ml' | 'g' } }>): string | null {
  if (items.length === 0) return null;
  const parts = [...items].sort((a, b) => a.product.name.localeCompare(b.product.name, 'pt-BR')).map(({ supply, product }) => {
    const name = product.name.charAt(0).toLowerCase() + product.name.slice(1);
    return product.unit === 'un' ? `${supply.qty} ${name}` : `${formatStockQty(product.unit, supply.qty)} de ${name}`;
  });
  return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} e ${parts.at(-1)}`;
}
